import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import FilterSelect, {
    FilterSelectItem as FilterSelectItemFromFilterSelect,
} from './filterSelect/FilterSelect';
import FilterChipBasicWithContextualMenu, {
    MenuItem as FilterChipMenuItemFromFilterChipBasicWithContextualMenu,
} from '../filterChipBasicWithContextualMenu/FilterChipBasicWithContextualMenu';
import FilterChipEnum, {
    FilterChipEnumItem,
    FilterChipEnumOnChange,
    FilterChipEnumValue,
} from '../filterChipEnum/FilterChipEnum';
import FilterChipDateTimeRange from '../filterChipDateTimeRange/FilterChipDateTimeRange';
// eslint-disable-next-line import/no-cycle
import FilterChipWithTextField from '../filterChipWithTextField/FilterChipWithTextField';
import FilterChipBoolean from '../filterChipBoolean/filterChipBoolean';

const useStyles = makeStyles((theme) => ({
    filterContainer: {
        display: 'flex',
        [theme.breakpoints.down('xs')]: {
            display: 'block',
        },
    },
    selectBox: {
        flexShrink: 0,
        width: theme.spacing(160 / 8),
        marginRight: theme.spacing(12 / 8),
        [theme.breakpoints.down('xs')]: {
            marginBottom: theme.spacing(12 / 8),
        },
    },
    chipsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        margin: theme.spacing(-6 / 8),
    },
    chipsItem: {
        margin: theme.spacing(6 / 8),
    },
}));

export type FilterChipMenuItem = FilterChipMenuItemFromFilterChipBasicWithContextualMenu;
export type FilterSelectItem = FilterSelectItemFromFilterSelect;

export enum ChipItemType {
    predefinedView = 'predefinedView',
    dateTimeRange = 'dateTimeRange',
    enum = 'enum',
    string = 'string',
    boolean = 'boolean',
    number = 'number',
}

interface ChipItemBasis {
    uniqueKey: string;
}

export interface ChipItemPredefinedView extends ChipItemBasis {
    type: ChipItemType.predefinedView;
    label: string;
    onRemove: () => void;
    items: FilterChipMenuItem[];
}

export interface ChipItemEnum extends ChipItemBasis {
    type: ChipItemType.enum;
    emptyAllowed?: boolean;
    items: FilterChipEnumItem[];
    label: string;
    onChange: FilterChipEnumOnChange;
    onRemove: () => void;
    value: FilterChipEnumValue;
}

export interface ChipItemDateTimeRange extends ChipItemBasis {
    type: ChipItemType.dateTimeRange;
    label: string;
    onChange: (value: string) => void;
    onRemove: () => void;
    value: string | null;
}

export interface ChipItemString extends ChipItemBasis {
    type: ChipItemType.string;
    label: string;
    onChange: (value: string) => void;
    onRemove: () => void;
    value: string | null;
}

export interface ChipItemNumber extends ChipItemBasis {
    type: ChipItemType.number;
    label: string;
    onChange: (value: number | undefined) => void;
    onRemove: () => void;
    value: number | null;
}

export interface ChipItemBoolean extends ChipItemBasis {
    type: ChipItemType.boolean;
    label: string;
    onChange: (value: number | undefined) => void;
    onRemove: () => void;
    value: string | number | null;
}

export type ChipItem =
    | ChipItemPredefinedView
    | ChipItemEnum
    | ChipItemDateTimeRange
    | ChipItemString
    | ChipItemNumber
    | ChipItemBoolean;

interface PropsBasic {
    chips: ChipItem[];
    name?: string;
}

interface PropsWithPredefinedViews extends PropsBasic {
    customFiltering?: FilterSelectItem[];
    predefinedViews: FilterSelectItem[];
}

interface PropsWithCustomFiltering extends PropsBasic {
    customFiltering: FilterSelectItem[];
    predefinedViews?: FilterSelectItem[];
}

type Props = PropsWithPredefinedViews | PropsWithCustomFiltering;

const Filter: React.FC<Props> = (props) => {
    const { chips, customFiltering, name, predefinedViews } = props;

    const classes = useStyles();

    return (
        <Box className={classes.filterContainer}>
            <div className={classes.selectBox}>
                {/* @ts-ignore is ok */}
                <FilterSelect
                    name={name}
                    predefinedViews={predefinedViews}
                    customFiltering={customFiltering}
                />
            </div>
            <div className={classes.chipsContainer}>
                {chips.map((chip, index) => {
                    if (chip.type === ChipItemType.predefinedView) {
                        return (
                            <div
                                className={classes.chipsItem}
                                key={
                                    'FilterChipBasicWithContextualMenu' +
                                    String(index)
                                }
                            >
                                <FilterChipBasicWithContextualMenu
                                    items={chip.items}
                                    key={chip.uniqueKey}
                                    label={chip.label}
                                    onRemove={chip.onRemove}
                                />
                            </div>
                        );
                    }
                    if (chip.type === ChipItemType.enum) {
                        return (
                            <div
                                className={classes.chipsItem}
                                key={'FilterChipEnum' + String(index)}
                            >
                                <FilterChipEnum
                                    emptyAllowed={chip.emptyAllowed}
                                    items={chip.items}
                                    key={chip.uniqueKey}
                                    label={chip.label}
                                    onChange={chip.onChange}
                                    onRemove={chip.onRemove}
                                    value={chip.value}
                                />
                            </div>
                        );
                    }
                    if (chip.type === ChipItemType.dateTimeRange) {
                        return (
                            <div
                                className={classes.chipsItem}
                                key={'FilterChipDateTimeRange' + String(index)}
                            >
                                <FilterChipDateTimeRange
                                    key={chip.uniqueKey}
                                    label={chip.label}
                                    onChange={chip.onChange}
                                    onRemove={chip.onRemove}
                                    value={chip.value}
                                />
                            </div>
                        );
                    }
                    if (chip.type === ChipItemType.string) {
                        return (
                            <div
                                className={classes.chipsItem}
                                key={'FilterChipWithTextField' + String(index)}
                            >
                                <FilterChipWithTextField
                                    key={chip.uniqueKey}
                                    label={chip.label}
                                    inputType={chip.type}
                                    onChange={chip.onChange}
                                    onRemove={chip.onRemove}
                                    value={chip.value}
                                />
                            </div>
                        );
                    }
                    if (chip.type === ChipItemType.number) {
                        return (
                            <div
                                className={classes.chipsItem}
                                key={'FilterChipWithTextField' + String(index)}
                            >
                                <FilterChipWithTextField
                                    key={chip.uniqueKey}
                                    label={chip.label}
                                    inputType={chip.type}
                                    onChange={chip.onChange}
                                    onRemove={chip.onRemove}
                                    value={chip.value}
                                />
                            </div>
                        );
                    }
                    if (chip.type === ChipItemType.boolean) {
                        return (
                            <div
                                className={classes.chipsItem}
                                key={'FilterChipBoolean' + String(index)}
                            >
                                <FilterChipBoolean
                                    key={chip.uniqueKey}
                                    label={chip.label}
                                    inputType={chip.type}
                                    onChange={chip.onChange}
                                    onRemove={chip.onRemove}
                                    value={chip.value}
                                />
                            </div>
                        );
                    }
                    throw Error('Unexpected filter type.');
                })}
            </div>
        </Box>
    );
};

export default Filter;
